import { SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, message, Row, Select, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DetailItem from "../../components/DetailItem";
import TitleSection from "../../components/TitleSection";
import { httpRequest } from "../../helpers/api";
import { getErrorMessage } from "../../helpers/errorHandler";
import { BaseResponsePaginationProps, BaseResponseProps } from "../../types/config.type";
import { EListingStatus, EListingType, ListingProperties } from "../../types/listing.type";
import NotSet from "../../components/NotSet";
import OfficeLabel from "../../components/OfficeLabel";
import useBreadcrumbs from "../../hooks/useBreadcrumbs";
import { TransferListingProperties } from "../../types/transferListing.type";
import { formatYearToTime } from "../../helpers/format";
import { doFilterItem } from "../transferEmployee/helper";
import ListingListTable from "../transferEmployee/DetailComponents/ListingListTable";
import ListingListGrid from "../transferEmployee/DetailComponents/ListingListGrid";
import { ReactComponent as ListUnordered } from "../../assets/Icons/ListUnordered.svg";
import { ReactComponent as MoreGrid } from "../../assets/Icons/MoreGrid.svg";

interface Params {
	transferListingId: string;
}

interface Props {
	transferListingId?: string;
	onDataLoaded?: (data: TransferListingProperties) => void;
	isModal?: boolean;
}

const { Title, Text } = Typography;
const TransferListingDetail: React.FC<Props> = ({ 
	transferListingId: propTransferListingId, 
	onDataLoaded,
	isModal = false 
}) => {
	const { setBreadcrumb } = useBreadcrumbs();
	const params = useParams<Params>();
	const transferListingId = propTransferListingId || params.transferListingId;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [transferListing, setTransferListing] = useState<TransferListingProperties>();
	const [listingsAgent, setListingsAgent] = useState<ListingProperties[]>([]);

	const [isMyListingGridView, setIsMyListingGridView] = useState<boolean>(true);
	const [searchMyListing, setSearchMyListing] = useState<string>("");
	const [filterMyListing, setFilterMyListing] = useState<string>("");

	const [searchTransferListing, setSearchTransferListing] = useState<string>("");
	const [filterTransferListing, setFilterTransferListing] = useState<EListingType | "">("");
	const [isTransferListingGridView, setIsTransferListingGridView] = useState<boolean>(true);

	const fetchData = async () => {
		if (!transferListingId) return;
		
		setIsLoading(true);
		try {
			let { data } = await httpRequest.get<BaseResponseProps<TransferListingProperties>>(
				"transfer-listings/" + transferListingId
			);

			let url =
				"listings" +
				"?filterUserId=" +
				data.payload.originListingUserId +
				"&limit=9999999999999" +
				"&includeData=listingPart,listingCertificateType,listingPropertyType,listingRenewHistory,listingTransferHistory" +
				"&filterStatus=" +
				[EListingStatus.EXPIRED, EListingStatus.PUBLISH, EListingStatus.UNPUBLISH].join(",");
			let { data: resListings } = await httpRequest.get<BaseResponsePaginationProps<ListingProperties>>(url);
			setListingsAgent(resListings.payload.results);
			
			if (!isModal) {
				let bc = [
					{
						label: data.payload.originListingUser.nickName,
						origin: transferListingId,
					},
				];
				setBreadcrumb(bc);
			}
			
			setTransferListing(data.payload);
			if (onDataLoaded) {
				onDataLoaded(data.payload);
			}
			setIsLoading(false);
		} catch (error) {
			message.error(getErrorMessage(error));
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (transferListingId) {
			fetchData();
		}

		// eslint-disable-next-line
	}, [transferListingId]);

	const MyListingFiltered = useMemo(
		() =>
			(transferListing?.metadata?.keepListingData ?? [])
				.filter((item) => doFilterItem(item, searchMyListing, filterMyListing))
				.map((item) => ({
					...item,
					agent: {
						...(transferListing?.metadata?.originUserData ?? { name: "" }),
					},
				})),
		[
			searchMyListing,
			filterMyListing,
			transferListing?.metadata?.keepListingData,
			transferListing?.metadata?.originUserData,
		]
	);

	const TransferedListingFiltered = useMemo(
		() =>
			(transferListing?.metadata?.transferListingData ?? [])
				.map((item) => ({
					...(item.listingData as ListingProperties),
					agent: {
						...(item.newUserData ?? { name: "" }),
					},
				}))
				.filter((item) => item && doFilterItem(item, searchTransferListing, filterTransferListing)),
		[searchTransferListing, filterTransferListing, transferListing?.metadata?.transferListingData]
	);

	return (
		<React.Fragment>
			{!isModal && (
				<TitleSection
					title="Detail Transfer Listing"
					icon="back"
				/>
			)}
			<Spin spinning={isLoading}>
				<Card
					size="small"
					bordered={false}
					style={isModal ? { boxShadow: 'none' } : undefined}
				>
					<Row
						gutter={[24, 0]}
						className="detail-bordered"
					>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="From Agent ID"
								value={transferListing?.originListingEmployee.employeeCode}
							/>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="From Agent Name"
								value={transferListing?.originListingUser.nickName}
							/>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem label="Office">
								{transferListing?.originListingOffice ? (
									<OfficeLabel office={transferListing.originListingOffice} />
								) : (
									<NotSet />
								)}
							</DetailItem>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="Total Transferred Listing"
								value={(transferListing?.metadata?.transferListingData ?? [])?.length}
								renderValue={(value) => value + " Listing"}
							/>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="Transferred At"
								value={transferListing?.createdAt as string}
								renderValue={(value) => formatYearToTime(value)}
							/>
						</Col>
					</Row>
					<Title
						level={5}
						style={{ marginTop: 20 }}
					>
						Listings
					</Title>

					<Row
						gutter={[10, 10]}
						align="middle"
						style={{ paddingBottom: 20 }}
					>
						<Col flex="auto">
							<Text strong={true}>
								Transfer Listings ({(transferListing?.metadata?.transferListingData ?? []).length}{" "}
								Listings)
							</Text>
						</Col>
						<Col>
							<Input
								placeholder="Search"
								style={{ width: 200 }}
								type="text"
								value={searchTransferListing}
								onChange={(e) => setSearchTransferListing(e.target.value)}
								suffix={<SearchOutlined />}
							/>
						</Col>
						<Col>
							<Select
								virtual={true}
								style={{ width: 200 }}
								value={filterTransferListing}
								onChange={setFilterTransferListing}
								placeholder="All"
								options={[
									{ value: "", label: "All" },
									{ value: EListingType.PRIMARY, label: "Primary" },
									{ value: EListingType.SECONDARY, label: "Secondary" },
								]}
							/>
						</Col>
						<Col>
							<Button
								onClick={() => setIsTransferListingGridView(!isTransferListingGridView)}
								className="btn-outlined"
								type="default"
								icon={isTransferListingGridView ? <MoreGrid /> : <ListUnordered />}
							/>
						</Col>
					</Row>
					{isTransferListingGridView ? (
						<ListingListGrid items={TransferedListingFiltered} />
					) : (
						<ListingListTable items={TransferedListingFiltered} />
					)}

					{/* <Row
						gutter={[10, 10]}
						align="middle"
						style={{ paddingBottom: 20, paddingTop: 40 }}
					>
						<Col flex="auto">
							<Text strong={true}>
								{transferListing?.metadata?.originUserData?.name} Keep Listings (
								{(transferListing?.metadata?.keepListingData ?? []).length} Listings)
							</Text>
						</Col>
						<Col>
							<Input
								placeholder="Search"
								style={{ width: 200 }}
								type="text"
								value={searchMyListing}
								onChange={(e) => setSearchMyListing(e.target.value)}
								suffix={<SearchOutlined />}
							/>
						</Col>
						<Col>
							<Select
								virtual={true}
								style={{ width: 200 }}
								value={filterMyListing}
								onChange={setFilterMyListing}
								placeholder="All"
								options={[
									{ value: "", label: "All" },
									{ value: EListingType.PRIMARY, label: "Primary" },
									{ value: EListingType.SECONDARY, label: "Secondary" },
								]}
							/>
						</Col>
						<Col>
							<Button
								onClick={() => setIsMyListingGridView(!isMyListingGridView)}
								className="btn-outlined"
								type="default"
								icon={isMyListingGridView ? <MoreGrid /> : <ListUnordered />}
							/>
						</Col>
					</Row>
					{isMyListingGridView ? (
						<ListingListGrid items={MyListingFiltered} />
					) : (
						<ListingListTable items={MyListingFiltered} />
					)} */}
				</Card>
			</Spin>
		</React.Fragment>
	);
};
export default TransferListingDetail;
